import { z } from 'zod';
import { ColumnMappingSchema, JobSettingsSchema } from './jobs.js';
import { PROVIDER_IDS } from './providers.js';
/**
 * The Zoho CRM contract: settings, import runs and push state.
 *
 * Split out of contract.ts, which carried the whole API contract in one file.
 * Nothing here changed on the way out.
 */
// ---------- Zoho CRM --------------------------------------------------------------
export const ZohoImportRequestSchema = z.object({
    mode: z.enum(['dry_run', 'apply']),
    modules: z
        .array(z.enum(['Accounts', 'Contacts', 'Leads']))
        .min(1)
        .optional(),
    resolveByName: z
        .object({ enabled: z.boolean(), creditBudget: z.number().int().min(0).max(100_000) })
        .optional(),
});
export const FromZohoJobRequestSchema = z.object({
    cvid: z.string().regex(/^\d{5,25}$/),
    name: z.string().min(1).max(120).optional(),
});
export const ConfirmJobRequestSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    columnMapping: ColumnMappingSchema,
    settings: JobSettingsSchema.partial(),
});
/**
 * How many contacts one enrichment request may carry.
 *
 * Both contact grids offer a select-all across every row, and a job holds far
 * more people than this was, so the ceiling is reachable by one click. Zod
 * rejects the whole request over it — not part of it — so the dialog has to stop
 * at exactly this number rather than offer a button that can only fail. That is
 * why it is exported rather than restated in the client: two copies of a limit
 * that must agree eventually disagree.
 *
 * Raised from 200 once the endpoint stopped preparing contacts one at a time. It
 * is bounded by the thirty-second API timeout, not by anything the provider
 * cares about, so what governs it is how much work happens per contact inside
 * that request.
 */
export const ENRICH_MAX_URLS = 2000;
export const EnrichRequestSchema = z.object({
    linkedinUrls: z.array(z.string()).min(1).max(ENRICH_MAX_URLS),
    fields: z.array(z.enum(['work_email', 'personal_email', 'phone'])).min(1),
    jobId: z.string().optional(),
    /**
     * Which source to buy from.
     *
     * Absent means the active provider, which is what every request meant before
     * there was a choice — so an older client keeps working rather than failing
     * on a field it does not know to send.
     *
     * The choice is per request because the cost is per contact. Discovery stays
     * account-wide for the opposite reason: a job whose rows came from two ladders
     * has two cost bases and two definitions of a match.
     */
    provider: z.enum(PROVIDER_IDS).optional(),
});
/**
 * Default persona presets. Size-tiered because in the real sample every
 * correctly identified person was founder-class and the technology group
 * contributed nobody unique while still costing credits for rejects.
 */
export const PRESETS = {
    founders_and_tech: {
        label: 'Founders, C-suite and technology leadership',
        personas: [
            {
                key: 'founders',
                label: 'Founders and C-suite',
                titleQuery: '(CEO || "Chief Executive Officer" || Founder || "Co-Founder" || "Managing Director" || Owner || President || COO) -"Vice President" -Assistant',
                pageSize: 3,
            },
            {
                key: 'tech',
                label: 'Technology leadership',
                titleQuery: '(CTO || CIO || CPO || "Chief Technology Officer" || "Chief Product Officer" || ((VP || Head || Director) && (Engineering || Technology || Product))) -Assistant',
                pageSize: 2,
            },
        ],
    },
    founders_only: {
        label: 'Founders and C-suite only',
        personas: [
            {
                key: 'founders',
                label: 'Founders and C-suite',
                titleQuery: '(CEO || "Chief Executive Officer" || Founder || "Co-Founder" || "Managing Director" || "Managing Partner" || Owner || President || COO || Proprietor) -"Vice President" -Assistant',
                pageSize: 5,
            },
        ],
    },
    tech_only: {
        label: 'Technology leadership only',
        personas: [
            {
                key: 'tech',
                label: 'Technology leadership',
                titleQuery: '(CTO || CIO || CPO || "Chief Technology Officer" || "Chief Information Officer" || ((VP || Head || Director) && (Engineering || Technology || Product || IT))) -Assistant',
                pageSize: 5,
            },
        ],
    },
    marketing_sales: {
        label: 'Marketing and sales leadership',
        personas: [
            {
                key: 'gtm',
                label: 'Marketing and sales',
                titleQuery: '(CMO || CRO || "Chief Marketing Officer" || "Chief Revenue Officer" || ((VP || Head || Director) && (Marketing || Sales || Growth || Revenue))) -Assistant',
                pageSize: 5,
            },
        ],
    },
};
/**
 * Yield distribution seeded from the real 15-company sample: 26.7% of companies
 * produced nobody, 40% produced one person, and the maximum was three. The
 * estimator uses this rather than quota times unit cost, which overstated by
 * about two and a half times.
 */
/**
 * How many companies one "find people again" request may carry.
 *
 * Every one is a fresh, paid search, so the ceiling is a guard against a stray
 * select-all rather than a limit of the machinery.
 */
export const COMPANY_RESEARCH_MAX = 200;
/**
 * Look for a company's people again, from the Companies screen.
 *
 * `providerId` is a source that can DISCOVER people - Apollo cannot. `presetId`
 * searches for a different set of roles than the job did; most companies with a
 * live site and a known LinkedIn page found nobody because every title returned
 * failed the job's persona, not because the company was never found.
 */
export const CompanyResearchRequestSchema = z
    .object({
    domains: z.array(z.string().min(1)).min(1).max(COMPANY_RESEARCH_MAX),
    providerId: z.enum(PROVIDER_IDS).optional(),
    presetId: z
        .string()
        .refine((id) => id in PRESETS, 'Unknown persona preset')
        .optional(),
    /** Run even where this exact search already found nobody. */
    repeat: z.boolean().optional(),
})
    .strict();
export const SEED_YIELD_DISTRIBUTION = { 0: 0.267, 1: 0.4, 2: 0.133, 3: 0.2 };
export function estimateCredits(companies, opts = {}) {
    const per = opts.creditsPerProfile ?? 4;
    const resolve = opts.resolveCost ?? 1.5;
    const dist = opts.distribution ?? SEED_YIELD_DISTRIBUTION;
    let meanPeople = 0;
    for (const [k, p] of Object.entries(dist))
        meanPeople += Number(k) * p;
    const mean = Math.round(companies * (resolve + meanPeople * per));
    return {
        mean,
        p10: Math.round(mean * 0.6),
        p90: Math.round(companies * (resolve + 3 * per)),
    };
}
//# sourceMappingURL=zoho.js.map